import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Input,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  IconButton,
  useColorModeValue,
  Alert,
  AlertIcon,
  Tooltip,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, ViewIcon, EditIcon } from "@chakra-ui/icons";
import { FiRefreshCw } from "react-icons/fi";
import { portfolioService } from "../services/edosService";

export default function PortfoliosPage() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDetailOpen,
    onOpen: onDetailOpen,
    onClose: onDetailClose,
  } = useDisclosure();
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("gray.50", "gray.700");

  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ portfolioName: "", description: "" });
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  const fetchPortfolios = useCallback(async () => {
    setLoading(true);
    try {
      const res = await portfolioService.list();
      setPortfolios(res.data.data || res.data.portfolios || []);
    } catch (err) {
      toast({ title: "Failed to load portfolios", status: "error" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);

  const handleOpenCreate = () => {
    setEditMode(false);
    setEditId(null);
    setForm({ portfolioName: "", description: "" });
    onOpen();
  };

  const handleOpenEdit = (p) => {
    setEditMode(true);
    setEditId(p._id);
    setForm({
      portfolioName: p.portfolioName,
      description: p.description || "",
    });
    onOpen();
  };

  const handleSubmit = async () => {
    if (!form.portfolioName) {
      toast({ title: "Portfolio name is required", status: "warning" });
      return;
    }
    setSubmitting(true);
    try {
      if (editMode) {
        await portfolioService.update(editId, form);
        toast({ title: "Portfolio updated", status: "success" });
      } else {
        await portfolioService.create(form);
        toast({ title: "Portfolio created", status: "success" });
      }
      onClose();
      fetchPortfolios();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed",
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleView = async (p) => {
    setSelectedPortfolio(null);
    onDetailOpen();
    setDetailLoading(true);
    try {
      const res = await portfolioService.getById(p._id);
      setSelectedPortfolio(res.data.data || res.data.portfolio || res.data);
    } catch (err) {
      toast({ title: "Failed to load portfolio details", status: "error" });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this portfolio?")) return;
    try {
      await portfolioService.delete(id);
      toast({ title: "Portfolio deleted", status: "success" });
      fetchPortfolios();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed",
        status: "error",
      });
    }
  };

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold">
            Portfolios
          </Text>
          <Text color="gray.500" fontSize="sm">
            Group and manage projects by portfolio
          </Text>
        </Box>
        <HStack>
          <IconButton
            icon={<FiRefreshCw />}
            variant="outline"
            aria-label="Refresh"
            onClick={fetchPortfolios}
          />
          <Button
            leftIcon={<AddIcon />}
            colorScheme="cyan"
            onClick={handleOpenCreate}
          >
            New Portfolio
          </Button>
        </HStack>
      </Flex>

      <Box
        bg={bg}
        border="1px"
        borderColor={borderColor}
        borderRadius="md"
        overflow="hidden"
      >
        {loading ? (
          <Flex justify="center" p={10}>
            <Spinner size="xl" color="cyan.500" />
          </Flex>
        ) : portfolios.length === 0 ? (
          <Alert status="info" m={4}>
            <AlertIcon />
            No portfolios yet. Create one to get started.
          </Alert>
        ) : (
          <Table variant="simple" size="sm">
            <Thead bg={headerBg}>
              <Tr>
                <Th>Portfolio Name</Th>
                <Th>Projects</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {portfolios.map((p) => (
                <Tr key={p._id}>
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="semibold" fontSize="sm">
                        {p.portfolioName}
                      </Text>
                      {p.description && (
                        <Text fontSize="xs" color="gray.500" noOfLines={1}>
                          {p.description}
                        </Text>
                      )}
                    </VStack>
                  </Td>
                  <Td>
                    <Badge colorScheme="cyan">
                      {p.projects?.length || p.projectCount || 0} projects
                    </Badge>
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={p.status === "active" ? "green" : "gray"}
                    >
                      {p.status || "active"}
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {p.createdAt
                        ? new Date(p.createdAt).toLocaleDateString()
                        : "-"}
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Tooltip label="View Details">
                        <IconButton
                          size="xs"
                          icon={<ViewIcon />}
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => handleView(p)}
                        />
                      </Tooltip>
                      <Tooltip label="Edit">
                        <IconButton
                          size="xs"
                          icon={<EditIcon />}
                          variant="ghost"
                          colorScheme="orange"
                          onClick={() => handleOpenEdit(p)}
                        />
                      </Tooltip>
                      <Tooltip label="Delete">
                        <IconButton
                          size="xs"
                          icon={<DeleteIcon />}
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDelete(p._id)}
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* Create / Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editMode ? "Edit Portfolio" : "Create Portfolio"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Portfolio Name</FormLabel>
                <Input
                  value={form.portfolioName}
                  onChange={(e) =>
                    setForm({ ...form, portfolioName: e.target.value })
                  }
                  placeholder="e.g. Digital Transformation"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="Optional description"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="cyan"
              isLoading={submitting}
              onClick={handleSubmit}
            >
              {editMode ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Detail Modal */}
      <Modal isOpen={isDetailOpen} onClose={onDetailClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedPortfolio?.portfolioName || "Portfolio Detail"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {detailLoading ? (
              <Flex justify="center" p={6}>
                <Spinner />
              </Flex>
            ) : selectedPortfolio ? (
              <VStack spacing={4} align="stretch">
                <SimpleGrid columns={3} spacing={3}>
                  <Box p={3} bg={headerBg} borderRadius="md">
                    <Stat>
                      <StatLabel>Total Projects</StatLabel>
                      <StatNumber>
                        {selectedPortfolio.projects?.length || 0}
                      </StatNumber>
                    </Stat>
                  </Box>
                  <Box p={3} bg={headerBg} borderRadius="md">
                    <Stat>
                      <StatLabel>Status</StatLabel>
                      <StatNumber fontSize="md">
                        {selectedPortfolio.status || "active"}
                      </StatNumber>
                    </Stat>
                  </Box>
                  <Box p={3} bg={headerBg} borderRadius="md">
                    <Stat>
                      <StatLabel>Created</StatLabel>
                      <StatNumber fontSize="sm">
                        {selectedPortfolio.createdAt
                          ? new Date(
                              selectedPortfolio.createdAt,
                            ).toLocaleDateString()
                          : "-"}
                      </StatNumber>
                    </Stat>
                  </Box>
                </SimpleGrid>

                {selectedPortfolio.projects?.length > 0 && (
                  <Box>
                    <Text fontWeight="semibold" mb={2}>
                      Projects
                    </Text>
                    <Table variant="simple" size="sm">
                      <Thead bg={headerBg}>
                        <Tr>
                          <Th>Project</Th>
                          <Th>Status</Th>
                          <Th>Type</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {selectedPortfolio.projects.map((proj) => (
                          <Tr key={proj._id}>
                            <Td>
                              <Text fontSize="sm">{proj.projectName}</Text>
                            </Td>
                            <Td>
                              <Badge colorScheme="green">{proj.status}</Badge>
                            </Td>
                            <Td>
                              <Text fontSize="sm">{proj.projectType}</Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </VStack>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onDetailClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
